/**
 * Key Derivation and Signing
 *
 * Derives secp256k1 key pairs from fuzzy extractor output and provides
 * signing functionality for challenge-response authentication.
 *
 * Security Properties:
 * - Keys are derived deterministically from master secret
 * - Domain binding prevents cross-site key reuse
 * - Signatures are standard secp256k1 ECDSA
 */

import * as secp from '@noble/secp256k1';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, hexToBytes } from '@noble/hashes/utils.js';
import type { KeyPair } from '../types';

// ============================================================================
// Key Derivation
// ============================================================================

/**
 * Derive a secp256k1 key pair from a master secret
 *
 * @param masterSecret - 32-byte master secret from fuzzy extractor
 * @param domain - Optional domain for site-specific keys
 * @returns KeyPair with private key, public key, and hex-encoded public key
 */
export function deriveKeyPair(masterSecret: Uint8Array, domain?: string): KeyPair {
  console.log('[ZK KeyDerivation] deriveKeyPair called');
  console.log('[ZK KeyDerivation] Master secret length:', masterSecret.length);
  console.log('[ZK KeyDerivation] Domain:', domain || '(none)');

  // Validate input
  if (masterSecret.length !== 32) {
    throw new Error(`Invalid master secret length: expected 32, got ${masterSecret.length}`);
  }

  // Derive domain-specific seed using HKDF
  const infoStr = domain ? `zk-auth-v1:${domain}` : 'zk-auth-v1';
  const info = new TextEncoder().encode(infoStr);
  console.log('[ZK KeyDerivation] HKDF info:', infoStr);

  const seed = hkdf(sha256, masterSecret, new Uint8Array(0), info, 32);
  console.log('[ZK KeyDerivation] HKDF seed derived, length:', seed.length);

  // Ensure seed is a valid secp256k1 private key
  // Private key must be in range [1, n-1] where n is the curve order
  const privateKey = ensureValidPrivateKey(seed);
  console.log('[ZK KeyDerivation] Private key validated');

  // Derive public key
  const publicKey = secp.getPublicKey(privateKey, true); // compressed format
  console.log('[ZK KeyDerivation] Public key derived, length:', publicKey.length);

  const result = {
    privateKey,
    publicKey,
    publicKeyHex: bytesToHex(publicKey),
  };

  console.log('[ZK KeyDerivation] Key pair created, public key hex:', result.publicKeyHex.substring(0, 20) + '...');

  return result;
}

/**
 * Ensure the seed is a valid secp256k1 private key
 * If not, hash it until we get a valid one
 */
function ensureValidPrivateKey(seed: Uint8Array): Uint8Array {
  let key = seed;
  let attempts = 0;
  const maxAttempts = 100;

  while (!secp.utils.isValidSecretKey(key) && attempts < maxAttempts) {
    key = sha256(key);
    attempts++;
  }

  if (!secp.utils.isValidSecretKey(key)) {
    throw new Error('Failed to derive valid private key');
  }

  return key;
}

// ============================================================================
// Signing
// ============================================================================

/**
 * Sign a challenge with the private key
 *
 * @param challenge - Challenge bytes or hex string
 * @param privateKey - 32-byte private key
 * @returns Signature as hex string
 */
export async function signChallenge(
  challenge: Uint8Array | string,
  privateKey: Uint8Array
): Promise<string> {
  // Convert challenge to bytes if string
  const challengeBytes = typeof challenge === 'string' ? hexToBytes(challenge) : challenge;

  // Hash the challenge (required for ECDSA)
  const messageHash = sha256(challengeBytes);

  // Sign the hash - signAsync returns a Signature object in v3
  const signature = await secp.signAsync(messageHash, privateKey);

  // Return compact signature as hex
  // In v3, signature is a Signature object with toBytes() method
  const sigBytes = (signature as any).toBytes ? (signature as any).toBytes() : signature;
  return bytesToHex(sigBytes);
}

/**
 * Verify a signature
 *
 * @param challenge - Original challenge bytes or hex string
 * @param signature - Signature hex string
 * @param publicKey - Public key bytes or hex string
 * @returns True if signature is valid
 */
export function verifySignature(
  challenge: Uint8Array | string,
  signature: string,
  publicKey: Uint8Array | string
): boolean {
  try {
    // Convert inputs to bytes
    const challengeBytes = typeof challenge === 'string' ? hexToBytes(challenge) : challenge;
    const publicKeyBytes = typeof publicKey === 'string' ? hexToBytes(publicKey) : publicKey;
    const signatureBytes = hexToBytes(signature);

    // Hash the challenge
    const messageHash = sha256(challengeBytes);

    // Verify
    return secp.verify(signatureBytes, messageHash, publicKeyBytes);
  } catch (error) {
    console.error('Signature verification error:', error);
    return false;
  }
}

// ============================================================================
// Domain Binding
// ============================================================================

/**
 * Get the current domain for key binding
 * Returns the origin (protocol + host) in browser, or a default in other environments
 */
export function getCurrentDomain(): string {
  if (typeof window !== 'undefined' && window.location) {
    return window.location.origin;
  }
  return 'default-domain';
}

/**
 * Derive a domain-bound key pair from master secret
 * Different domains will produce different keys, preventing cross-site tracking
 */
export function deriveDomainBoundKeyPair(masterSecret: Uint8Array): KeyPair {
  const domain = getCurrentDomain();
  return deriveKeyPair(masterSecret, domain);
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Generate a random challenge
 * Note: This is typically done server-side, but provided for testing
 */
export function generateChallenge(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return bytesToHex(bytes);
}

/**
 * Convert public key to Ethereum-style address (for Web3 compatibility)
 */
export function publicKeyToAddress(publicKey: Uint8Array | string): string {
  const publicKeyBytes = typeof publicKey === 'string' ? hexToBytes(publicKey) : publicKey;

  // For compressed public keys, we need to decompress first
  // Then take keccak256 of the uncompressed key (without prefix) and take last 20 bytes
  // This is a simplified version - for production use ethers.js

  // Hash the public key
  const hash = sha256(publicKeyBytes);

  // Take last 20 bytes and format as address
  const addressBytes = hash.slice(-20);
  return '0x' + bytesToHex(addressBytes);
}

/**
 * Encode key pair for storage (only public parts)
 */
export function encodePublicKeyPair(keyPair: KeyPair): { publicKey: string } {
  return {
    publicKey: keyPair.publicKeyHex,
  };
}
